import { AlertTriangle, Barcode, Camera, PencilLine, RefreshCw, Save, Star, Trash2, Undo2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button, ModalFrame, ScreenInfo } from '@krp/ui-kit'

import '../../../shared/ui/onec.css'
import { CommandStatus } from '../../../shared/ui/CommandStatus'
import { InputField } from '../../../shared/ui/InputField'
import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import { useUnsavedGuard } from '../../../shared/ui/useUnsavedGuard'
import type { ProductPhotosActions, ProductPhotosVm } from '../../photos/model/types'
import { ProductPhotoGallery } from '../../photos/ui/ProductPhotoGallery'
import type { ProductFactsVm } from '../../pricing/model/productFacts'
import { ProductFacts } from '../../pricing/ui/ProductFacts'
import type { FieldVm, ProductCardActions, ProductCardVm } from '../model/types'
import { BarcodeScanner } from './scanner/BarcodeScanner'

/** Справка карточки: одна ⓘ сверху слева, обычными словами; у отдельных кнопок своих значков нет. */
const CARD_INFO_LINES = [
  'Карточка товара: фото, наименование, артикул, штрихкоды, цены и остатки.',
  'Наименование и артикул можно править: внизу появятся «Сохранить» и «Отменить изменения». Закрыть окно с несохранённой правкой можно только после подтверждения.',
  'Цены и остатки здесь только для просмотра. Закупки записываются через поставки, а не из карточки товара.',
] as const

function noteOf(field: FieldVm): string | null {
  if (field.conflict) return `Изменено и здесь, и в 1С. В 1С сейчас: «${field.server ?? ''}»`
  if (field.changed) return `Изменено, не сохранено. Было: «${field.server ?? '—'}»`
  return null
}

const timeOf = (iso: string | null): string => {
  if (iso === null) return ''
  const date = new Date(iso)
  return Number.isNaN(date.getTime())
    ? ''
    : `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

/**
 * Карточка товара одной базы 1С, вёрстка от телефона (360 px): один столбец, крупные цели нажатия, всё переносится,
 * прокрутка внутри окна, действия — в липкой панели внизу.
 *
 * Сверху — крестик окна (рамка), внизу одна кнопка закрытия, и только когда правки нет: «Закрыть». Есть правка —
 * внизу «Сохранить» и «Отменить изменения» (с подтверждением «Да / Нет»). Закрытие окна (крестик, Esc) с несохранённой
 * правкой тоже спрашивает: экран ставит сторожа закрытия окна (`actions.closeGuard`).
 *
 * Фото — слайдер с плиткой «Добавить»; цены и остатки — только чтение (`facts`), закупок в карточке нет. Черновик —
 * дельта поверх значений 1С; если поле поменяли в 1С во время правки, экран показывает обе версии и ждёт решения
 * человека.
 */
export function ProductCardScreen({
  vm,
  actions,
  facts,
  photos,
}: {
  vm: ProductCardVm
  actions: ProductCardActions
  /** Сводка цен и остатков (чтение); без неё карточка остаётся карточкой правки. */
  facts?: ProductFactsVm
  /** Фото товара: vm и действия фичи фото; без них фото в карточке нет. */
  photos?: { vm: ProductPhotosVm; actions: ProductPhotosActions }
}) {
  const [confirmClose, setConfirmClose] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [newBarcode, setNewBarcode] = useState('')
  const [scanning, setScanning] = useState(false)
  // Черновик или команда без итога: перезагрузка потеряла бы и то и другое.
  useUnsavedGuard(vm.dirty || vm.locked)

  // Сторож закрытия окна: пока есть несохранённая правка, крестик рамки и Esc сперва спрашивают.
  const { closeGuard } = actions
  useEffect(() => {
    if (!vm.dirty) {
      closeGuard(null)
      return undefined
    }
    closeGuard(() => {
      setConfirmClose(true)
      return false
    })
    return () => closeGuard(null)
  }, [closeGuard, vm.dirty])

  const writable = vm.card !== null && (vm.configurationVersion ?? '') !== '' && !vm.markedForDeletion
  const editing = vm.dirty
  const save = () => {
    if (vm.canSave) actions.save()
  }
  const addBarcode = (code: string) => {
    if (code.trim() === '') return
    actions.addBarcode(code)
    setNewBarcode('')
  }
  /** Закрыть окно после ответа человека: сторож снимается, иначе он спросил бы ещё раз. */
  const closeNow = () => {
    closeGuard(null)
    setConfirmClose(false)
    actions.close()
  }

  return (
    <section className="onec-screen onec-card" aria-label={vm.card?.name ?? 'Товар 1С'}>
      <header className="onec-card__head">
        <ScreenInfo title="Карточка товара" lines={CARD_INFO_LINES} />
        <h2>{vm.card?.name ?? 'Товар 1С'}</h2>
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} busy={vm.query.refreshing} onClick={actions.reload}>
          Обновить
        </Button>
      </header>

      <OneCQueryState query={vm.query} emptyText="Товар не найден" onRetry={actions.reload} />

      {vm.markedForDeletion && (
        <div className="onec-banner" role="status">
          <Trash2 size={18} aria-hidden="true" />
          <p>Товар помечен на удаление в 1С. Правка закрыта.</p>
        </div>
      )}

      {vm.dirty && (
        <div className="onec-banner" role="status">
          <PencilLine size={18} aria-hidden="true" />
          <p>
            Есть несохранённые изменения{vm.draftStartedAt !== null ? ` (черновик с ${timeOf(vm.draftStartedAt)})` : ''}.
          </p>
        </div>
      )}

      {vm.conflicts.length > 0 && (
        <div className="onec-section" role="alert">
          <h3>
            <AlertTriangle size={16} aria-hidden="true" /> В 1С изменили эти поля, пока вы правили
          </h3>
          <ul className="onec-conflicts">
            {vm.conflicts.map((conflict) => (
              <li key={conflict.field} className="onec-conflicts__item">
                <p className="onec-conflicts__field">{conflict.label}</p>
                <dl className="onec-conflicts__values">
                  <div>
                    <dt>Было</dt>
                    <dd>{conflict.base || '—'}</dd>
                  </div>
                  <div>
                    <dt>В 1С сейчас</dt>
                    <dd>{conflict.server || '—'}</dd>
                  </div>
                  <div>
                    <dt>Ваше</dt>
                    <dd>{conflict.mine || '—'}</dd>
                  </div>
                </dl>
                <Button onClick={() => actions.takeServer(conflict.field)}>Взять из 1С</Button>
              </li>
            ))}
          </ul>
          <div className="onec-actions">
            <Button variant="primary" onClick={actions.keepMine}>
              Оставить мои значения
            </Button>
          </div>
        </div>
      )}

      {vm.card !== null && photos !== undefined && <ProductPhotoGallery vm={photos.vm} actions={photos.actions} />}

      {vm.card !== null && (
        <form
          className="onec-form"
          onSubmit={(event) => {
            event.preventDefault()
            save()
          }}
        >
          <InputField
            label="Наименование"
            multiline
            onEnter={save}
            value={vm.form.name.value}
            onChange={(value) => actions.fieldChanged('name', value)}
            disabled={vm.locked || vm.markedForDeletion}
            note={noteOf(vm.form.name)}
          />
          <InputField
            label="Артикул"
            value={vm.form.article.value}
            onChange={(value) => actions.fieldChanged('article', value)}
            onEnter={save}
            disabled={vm.locked || vm.markedForDeletion}
            note={noteOf(vm.form.article)}
          />
        </form>
      )}

      {vm.card !== null && facts !== undefined && <ProductFacts vm={facts} onRetry={actions.reload} />}

      {vm.card !== null && (
        <section className="onec-section" aria-labelledby="onec-barcodes-title">
          <h3 id="onec-barcodes-title">
            <Barcode size={16} aria-hidden="true" /> Штрихкоды
          </h3>
          <OneCQueryState query={vm.barcodesQuery} emptyText="Штрихкодов нет" onRetry={actions.reload} />
          {vm.barcodes.length > 0 && (
            <ul className="onec-list">
              {vm.barcodes.map((row) => (
                <li key={row.barcode}>
                  <span className="onec-list__main">
                    {row.isMain && <Star size={14} aria-hidden="true" />} {row.barcode}
                    {row.isMain ? ' — основной' : ''}
                  </span>
                  {!row.isMain && (
                    <Button disabled={!writable || row.busy} onClick={() => actions.setMainBarcode(row.barcode)}>
                      Сделать основным
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    icon={<Trash2 size={16} aria-hidden="true" />}
                    disabled={!writable || row.busy}
                    onClick={() => actions.deleteBarcode(row.barcode)}
                    aria-label={`Удалить штрихкод ${row.barcode}`}
                  >
                    Удалить
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="onec-search">
            <InputField
              label="Новый штрихкод"
              value={newBarcode}
              inputMode="numeric"
              onChange={setNewBarcode}
              onEnter={() => addBarcode(newBarcode)}
              disabled={!writable}
            />
            <div className="onec-search__buttons">
              <Button disabled={!writable || newBarcode.trim() === ''} onClick={() => addBarcode(newBarcode)}>
                Добавить
              </Button>
              <Button icon={<Camera size={16} aria-hidden="true" />} disabled={!writable} onClick={() => setScanning(true)}>
                Сканировать
              </Button>
            </div>
          </div>
        </section>
      )}

      {vm.card !== null && !vm.markedForDeletion && (
        <section className="onec-section" aria-label="Пометка на удаление">
          {confirmDelete ? (
            <div className="onec-banner" role="alertdialog" aria-label="Подтверждение пометки">
              <AlertTriangle size={18} aria-hidden="true" />
              <div>
                <p>Пометить «{vm.card.name}» на удаление в 1С? Товар не удаляется физически.</p>
                <div className="onec-actions">
                  <Button onClick={() => setConfirmDelete(false)}>Отмена</Button>
                  <Button
                    variant="primary"
                    disabled={!writable}
                    onClick={() => {
                      setConfirmDelete(false)
                      actions.markForDeletion()
                    }}
                  >
                    Пометить на удаление
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <Button variant="ghost" icon={<Trash2 size={16} aria-hidden="true" />} disabled={!writable} onClick={() => setConfirmDelete(true)}>
              Пометить на удаление
            </Button>
          )}
        </section>
      )}

      <footer className="onec-card__bar" aria-label="Действия с карточкой">
        {vm.commands.length > 0 && (
          <div className="onec-card__results">
            {vm.commands.map((command) => (
              <CommandStatus
                key={command.key}
                command={command}
                onResend={() => actions.resend(command.key)}
                onDismiss={() => actions.dismiss(command.key)}
              />
            ))}
          </div>
        )}
        {editing && vm.saveHint !== null && <p className="onec-field__note">{vm.saveHint}</p>}
        <div className="onec-card__buttons">
          {editing ? (
            <>
              <Button type="button" variant="primary" icon={<Save size={16} aria-hidden="true" />} disabled={!vm.canSave} onClick={save}>
                Сохранить
              </Button>
              <Button
                type="button"
                icon={<Undo2 size={16} aria-hidden="true" />}
                disabled={vm.locked}
                onClick={() => setConfirmDiscard(true)}
              >
                Отменить изменения
              </Button>
            </>
          ) : (
            <Button type="button" onClick={actions.close}>
              Закрыть
            </Button>
          )}
        </div>
      </footer>

      {confirmDiscard && (
        <ModalFrame title="Отменить изменения?" size="sm" covered={false} onClose={() => setConfirmDiscard(false)}>
          <div className="onec-confirm" role="alertdialog" aria-label="Отмена изменений">
            <p>Отменить несохранённые изменения?</p>
            <div className="onec-actions">
              <Button onClick={() => setConfirmDiscard(false)}>Нет</Button>
              <Button
                variant="primary"
                onClick={() => {
                  setConfirmDiscard(false)
                  actions.discard()
                }}
              >
                Да
              </Button>
            </div>
          </div>
        </ModalFrame>
      )}

      {confirmClose && (
        <ModalFrame title="Закрыть карточку?" size="sm" covered={false} onClose={() => setConfirmClose(false)}>
          <div className="onec-confirm" role="alertdialog" aria-label="Несохранённые изменения">
            <p>Изменения не сохранены. Закрыть окно?</p>
            <p className="onec-field__note">Черновик останется до перезагрузки страницы — его можно будет дописать.</p>
            <div className="onec-actions">
              <Button onClick={() => setConfirmClose(false)}>Остаться</Button>
              <Button variant="primary" onClick={closeNow}>
                Закрыть, черновик оставить
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  actions.discard()
                  closeNow()
                }}
              >
                Закрыть и отменить изменения
              </Button>
            </div>
          </div>
        </ModalFrame>
      )}

      {scanning && (
        <BarcodeScanner
          onClose={() => setScanning(false)}
          onCode={(code) => {
            setScanning(false)
            setNewBarcode(code)
          }}
        />
      )}
    </section>
  )
}
